import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * A snapshot of the wizard's navigationally-relevant state. Two snapshots are
 * compared to figure out which wizard action bridges them.
 */
interface WizardSnapshot {
  /** Wizard step number (1...totalSteps). */
  step: number;
  /** Whether the mobile intro screen is currently visible. */
  displayIntro: boolean;
  /** Whether the viewport is currently in the mobile breakpoint. */
  isMobile: boolean;
  /**
   * Active sub-step within wizard step 2. Only meaningful when `step === 2`;
   * stored unconditionally so it can be restored on re-entry.
   *
   * Subflow A (always):             1a → 1b
   * Subflow B (aiSelect === false): 2a → 2b → 2c
   */
  step2Sub: '1a' | '1b' | '2a' | '2b' | '2c';
  /**
   * AI vs manual sub-screen toggle inside step 3. Only meaningful when
   * `step === 3`; stored unconditionally so it can be restored on re-entry.
   */
  aiSelectLogo: boolean;
}

const HASH_PREFIX = 'step=';

/**
 * Compute the human-visible step label for a snapshot.
 *
 *   - mobile intro screen  → 0
 *   - step 1               → 1
 *   - step 2 sub '1a'      → 2
 *   - step 2 sub '1b'      → 3
 *   - step 2 sub '2a'      → 4
 *   - step 2 sub '2b'      → 5
 *   - step 2 sub '2c'      → 6  (domain connect)
 *   - step 3 (AI)          → 7
 *   - step 3 (manual)      → 8
 *   - step >= 4 (finish)   → step + 6  (avoids collisions)
 */
const STEP2_SUB_LABEL: Record<'1a' | '1b' | '2a' | '2b' | '2c', number> = {
  '1a': 2,
  '1b': 3,
  '2a': 4,
  '2b': 5,
  '2c': 6,
};

const positionLabel = (snap: WizardSnapshot): number => {
  if (snap.isMobile && snap.displayIntro && snap.step === 1) {
    return 0;
  }
  if (snap.step === 2) {
    // On desktop all sub-steps share the same history slot.
    return snap.isMobile ? STEP2_SUB_LABEL[snap.step2Sub] : 2;
  }
  if (snap.step === 3) {
    // Desktop collapses step 3 to a single slot; mobile distinguishes the two
    // modes so the URL reflects which screen is showing.
    if (!snap.isMobile) return 3;
    return snap.aiSelectLogo ? 7 : 8;
  }
  if (snap.step >= 4) {
    return snap.step + 6;
  }
  return snap.step;
};

const snapshotsEqual = (a: WizardSnapshot, b: WizardSnapshot): boolean => {
  if (a.step !== b.step) return false;
  if (a.displayIntro !== b.displayIntro) return false;
  // step2Sub is only a navigational axis on mobile.
  if (a.step === 2 && a.isMobile && a.step2Sub !== b.step2Sub) return false;
  // aiSelectLogo is only a navigational axis on mobile (single slot on desktop).
  if (a.step === 3 && a.isMobile && a.aiSelectLogo !== b.aiSelectLogo) return false;
  return true;
};

/** Parse the position label from a location hash, or `null` if absent/invalid. */
const parsePositionFromHash = (hash: string): number | null => {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw.startsWith(HASH_PREFIX)) {
    return null;
  }
  const value = Number(raw.slice(HASH_PREFIX.length));
  return Number.isFinite(value) ? value : null;
};

const buildHash = (position: number): string => `#${HASH_PREFIX}${position}`;

/**
 * Which wizard action bridges two adjacent snapshots.
 */
type WizardAction = 'next' | 'prev' | 'step2SubNext' | 'step2SubPrev' | 'step2SelectDomain';

type Step2Family = 'A' | 'B';
const SUBFLOW_A: Array<WizardSnapshot['step2Sub']> = ['1a', '1b'];
const SUBFLOW_B: Array<WizardSnapshot['step2Sub']> = ['2a', '2b', '2c'];
const step2Family = (sub: WizardSnapshot['step2Sub']): Step2Family => (sub === '1a' || sub === '1b' ? 'A' : 'B');

/**
 * True when two snapshots differ only by an in-place subflow/mode *toggle*
 * (step 2's AI⇄manual or step 3's AI⇄manual) rather than a next/prev move.
 * Such transitions `replace` the current history slot and are never bridged by
 * browser back/forward. Mobile only — on desktop a toggle doesn't change the
 * snapshot at all (see snapshotsEqual / positionLabel).
 */
const isToggleTransition = (from: WizardSnapshot, to: WizardSnapshot): boolean => {
  if (from.step !== to.step || !from.isMobile) return false;
  if (from.step === 2) return step2Family(from.step2Sub) !== step2Family(to.step2Sub);
  if (from.step === 3) return from.aiSelectLogo !== to.aiSelectLogo;
  return false;
};

const actionBetween = (from: WizardSnapshot, to: WizardSnapshot): WizardAction | null => {
  // Moving between sub-steps of step 2 — mobile only, and only *within* the
  // same subflow. A cross-subflow change is a toggle (replace), not a bridged
  // next/prev, so we don't translate it here.
  if (from.step === 2 && to.step === 2 && from.isMobile && from.step2Sub !== to.step2Sub) {
    if (step2Family(from.step2Sub) !== step2Family(to.step2Sub)) return null;
    const order = step2Family(from.step2Sub) === 'A' ? SUBFLOW_A : SUBFLOW_B;
    const fi = order.indexOf(from.step2Sub);
    const ti = order.indexOf(to.step2Sub);
    if (ti === fi + 1) {
      // The domain-connect substep (2c / 2b3) is only ever entered via the
      // dedicated onStep2SelectDomain handler, never the generic next.
      return to.step2Sub === '2c' ? 'step2SelectDomain' : 'step2SubNext';
    }
    if (ti === fi - 1) return 'step2SubPrev';
    return null; // non-adjacent jump — we don't bridge these
  }
  // Toggling the mobile intro screen.
  if (from.step === 1 && to.step === 1 && from.displayIntro !== to.displayIntro) {
    return to.displayIntro ? 'prev' : 'next';
  }
  // Moving between adjacent main steps.
  if (to.step === from.step + 1 && !to.displayIntro && !from.displayIntro) {
    return 'next';
  }
  if (to.step === from.step - 1 && !to.displayIntro && !from.displayIntro) {
    return 'prev';
  }
  return null;
};

interface UseBrowserHistorySyncOptions {
  step: number;
  displayIntro: boolean;
  isMobile: boolean;
  aiSelectLogo: boolean;
  step2Sub: '1a' | '1b' | '2a' | '2b' | '2c';
  /** True while the wizard's initial data is still being loaded. */
  loading: boolean;
  onNextStep: () => void;
  onPrevStep: () => void;
  /** Advances within step 2's subflow (mobile) — used to bridge browser-forward. */
  onStep2Next: () => void;
  /** Enters the domain-connect substep (2b3 / '2c') — the only path into it. */
  onStep2SelectDomain: () => void;
}

/**
 * Synchronizes the Deploy wizard's step navigation with the browser history,
 * so the browser's Back/Forward buttons (and gestures) move the user through
 * the wizard's steps — and ONLY through the wizard's steps.
 *
 * Step 2 has two subflows:
 *   Subflow A (always):             1a → 1b
 *   Subflow B (aiSelect === false): 2a → 2b → 2c
 * The navigation callbacks decide which subflow is active.
 *
 * Step 3 is a single screen whose identity (AI vs manual) is set by the
 * aiSelectLogo toggle; toggling `replace`s the history slot rather than pushing.
 */
export const useBrowserHistorySync = ({
  step,
  displayIntro,
  isMobile,
  aiSelectLogo,
  step2Sub,
  loading,
  onNextStep,
  onPrevStep,
  onStep2Next,
  onStep2SelectDomain,
}: UseBrowserHistorySyncOptions): void => {
  const navigate = useNavigate();
  const location = useLocation();

  const onNextStepRef = React.useRef(onNextStep);
  const onPrevStepRef = React.useRef(onPrevStep);
  const onStep2NextRef = React.useRef(onStep2Next);
  const onStep2SelectDomainRef = React.useRef(onStep2SelectDomain);
  React.useEffect(() => {
    onNextStepRef.current = onNextStep;
    onPrevStepRef.current = onPrevStep;
    onStep2NextRef.current = onStep2Next;
    onStep2SelectDomainRef.current = onStep2SelectDomain;
  }, [onNextStep, onPrevStep, onStep2Next, onStep2SelectDomain]);

  const ownedStackRef = React.useRef<WizardSnapshot[]>([]);
  const ownedIndexRef = React.useRef<number>(-1);
  const pendingUrlTargetRef = React.useRef<WizardSnapshot | null>(null);

  // Initialize: seed the URL with the current snapshot once loading completes.
  React.useEffect(() => {
    if (loading) return;
    if (ownedIndexRef.current !== -1) return;

    const initial: WizardSnapshot = { step, displayIntro, isMobile, aiSelectLogo, step2Sub };
    ownedStackRef.current = [initial];
    ownedIndexRef.current = 0;

    const targetLabel = positionLabel(initial);
    const existing = parsePositionFromHash(location.hash);
    if (existing !== targetLabel) {
      navigate(
        { pathname: location.pathname, search: location.search, hash: buildHash(targetLabel) },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // React to wizard state changes by updating router history.
  React.useEffect(() => {
    if (loading) return;
    if (ownedIndexRef.current === -1) return;

    const nextSnap: WizardSnapshot = { step, displayIntro, isMobile, aiSelectLogo, step2Sub };

    if (pendingUrlTargetRef.current !== null && snapshotsEqual(pendingUrlTargetRef.current, nextSnap)) {
      pendingUrlTargetRef.current = null;
      return;
    }

    const stack = ownedStackRef.current;
    const index = ownedIndexRef.current;
    const currentSnap = stack[index];
    if (snapshotsEqual(currentSnap, nextSnap)) return;

    // Case 0: an in-place subflow/mode toggle — replace the current history slot
    // (no new entry) and drop any forward slots invalidated by the toggle's
    // reset cascade. See wizard-navigation.md §5–6.
    if (isToggleTransition(currentSnap, nextSnap)) {
      const truncated = stack.slice(0, index + 1);
      truncated[index] = nextSnap;
      ownedStackRef.current = truncated;
      navigate(
        { pathname: location.pathname, search: location.search, hash: buildHash(positionLabel(nextSnap)) },
        { replace: true },
      );
      return;
    }

    // Case 1: moved back to the previous slot — real browser back.
    if (index > 0 && snapshotsEqual(stack[index - 1], nextSnap)) {
      ownedIndexRef.current = index - 1;
      pendingUrlTargetRef.current = nextSnap;
      navigate(-1);
      return;
    }

    // Case 2: moved forward to the next slot — real browser forward.
    if (index < stack.length - 1 && snapshotsEqual(stack[index + 1], nextSnap)) {
      ownedIndexRef.current = index + 1;
      pendingUrlTargetRef.current = nextSnap;
      navigate(1);
      return;
    }

    // Case 3: new position — truncate stale forward slots and push.
    const truncated = stack.slice(0, index + 1);
    truncated.push(nextSnap);
    ownedStackRef.current = truncated;
    ownedIndexRef.current = truncated.length - 1;

    navigate({
      pathname: location.pathname,
      search: location.search,
      hash: buildHash(positionLabel(nextSnap)),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, step, displayIntro, isMobile, aiSelectLogo, step2Sub]);

  // React to URL changes (browser back/forward).
  React.useEffect(() => {
    if (loading) return;
    if (ownedIndexRef.current === -1) return;

    const targetLabel = parsePositionFromHash(location.hash);
    if (targetLabel === null) return;

    const stack = ownedStackRef.current;
    const index = ownedIndexRef.current;
    const currentSnap = stack[index];
    if (positionLabel(currentSnap) === targetLabel) return;

    let newIndex: number | null = null;
    if (index > 0 && positionLabel(stack[index - 1]) === targetLabel) {
      newIndex = index - 1;
    } else if (index < stack.length - 1 && positionLabel(stack[index + 1]) === targetLabel) {
      newIndex = index + 1;
    }

    if (newIndex === null) return;

    const targetSnap = stack[newIndex];
    const action = actionBetween(currentSnap, targetSnap);
    if (action === null) return;

    ownedIndexRef.current = newIndex;
    pendingUrlTargetRef.current = targetSnap;

    if (action === 'step2SubNext') {
      onStep2NextRef.current();
    } else if (action === 'step2SelectDomain') {
      onStep2SelectDomainRef.current();
    } else if (action === 'next') {
      onNextStepRef.current();
    } else {
      // 'prev' and 'step2SubPrev' both retreat — onPrevStep handles substeps.
      onPrevStepRef.current();
    }
  }, [loading, location.hash]);
};
